/**
 * Permission checking utility
 * Checks if the current user has the required permission(s)
 */

/**
 * Get current user from localStorage
 * @returns {Object|null} User object with permissions array
 */
export const getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    return JSON.parse(userStr);
  } catch (error) {
    console.error('Error parsing user from localStorage:', error);
    return null;
  }
};

/**
 * Check if user has a specific permission
 * @param {string} permission - Permission string to check (e.g., 'products.create')
 * @returns {boolean} True if user has the permission
 */
export const hasPermission = (permission) => {
  const user = getCurrentUser();
  if (!user || !user.permissions || !Array.isArray(user.permissions)) {
    return false;
  }
  return user.permissions.includes(permission);
};

/**
 * Check if user has any of the specified permissions
 * @param {string[]} permissions - Array of permission strings
 * @returns {boolean} True if user has at least one of the permissions
 */
export const hasAnyPermission = (permissions) => {
  if (!Array.isArray(permissions) || permissions.length === 0) {
    return true; // No permissions required
  }
  return permissions.some(permission => hasPermission(permission));
};

/**
 * Check if user has all of the specified permissions
 * @param {string[]} permissions - Array of permission strings
 * @returns {boolean} True if user has all of the permissions
 */
export const hasAllPermissions = (permissions) => {
  if (!Array.isArray(permissions) || permissions.length === 0) {
    return true; // No permissions required
  }
  return permissions.every(permission => hasPermission(permission));
};

/**
 * React hook for permission checking
 * @returns {Object} Permission checking functions
 */
export const usePermissionCheck = () => {
  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getCurrentUser
  };
};
